/**
 * Centralizes all analytics event tracking for the Coverage component.
 * Deduplicates the "mark as completed" payload previously built identically
 * in both HappyFlow and CommentRpa save handlers.
 */
import { useSelector } from "react-redux";
import { useInitialDataOfSingleMedicalOrder } from "../../../../../context/initialDataOfSingleMedicalOrder";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../../context/payerNameContextFromSingleOrderLayout";
import {
  AuthOnFile,
  Demographics,
  GeneralOrderDetails,
  SingleCoverage,
  Status,
} from "../../../../../data-model/medicalPaOrdersModel";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../../../../enums/eventsData";
import { EventsName } from "../../../../../enums/eventsName";
import { useLogJCodeStatusEvents } from "../../../../../hooks/logJCodeStatusEvents";
import { Submission } from "../../../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { RootState } from "../../../../../redux/store/store";
import { checkIfBusinessOfficeNotesAreEmpty } from "../../../../../utils/busniessOfficeNotesHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../../workerManagers/workerManager";
import { getAllJCodesFromAuthOnFile } from "../../../../singleOrderLayout/utils/existingDocsToLs";

/**
 * Provides analytics event helpers for the Coverage screen.
 * Tracks tab selections, mark-as-completed events, and J-code logging.
 *
 * @param orderId - The current order identifier
 * @param demographicsData - Patient demographic information
 * @param authOnFileDetails - Authorization-on-file details including auth status
 * @param submissionData - Submission record for ICD codes and regimen state
 * @param status - Current order status object
 * @param coverage - Array of coverage entries for the order
 * @param generalOrderDetails - General order details for J-code event logging
 * @returns Analytics tracking functions and BigQuery enum references
 */
export const useCoverageAnalytics = (
  orderId: string,
  demographicsData: Demographics,
  authOnFileDetails: AuthOnFile,
  submissionData: Submission,
  status: Status,
  coverage: SingleCoverage[],
  generalOrderDetails?: GeneralOrderDetails,
) => {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  // const { singleMedicalPaOrder } = useSelector(
  //   (state: RootState) => state.singleMedicalPaOrder,
  // );
  const org_id = getOrgIdForFetchExternalWorklist();
  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const { oldAuthStatus: oldAuthStatusFromContext, oldPrimaryIcdCodes } =
    useInitialDataOfSingleMedicalOrder();
  const logJCodeStatusEvents = useLogJCodeStatusEvents(
    generalOrderDetails as GeneralOrderDetails,
    coverage,
    submissionData,
  );

  const primaryCoverage = coverage?.find(
    (coverage) => coverage.coverage_type === "primary",
  );

  const getBasePayload = () => ({
    patient_id: demographicsData?.patient_id ?? "",
    order_id: orderId ?? "",
    org_id: org_id ?? "",
    user_id: user?.id ?? "",
    user_email: user?.email ?? "",
  });

  /**
   * Fires the INSURANCE_TAB_SELECTED event when a coverage tab is viewed.
   *
   * @param tabLabel - Display label of the selected tab
   * @param tabIndex - Zero-based index of the selected tab
   * @param primaryInsuranceStatus - The insurance status value to record
   */
  const trackInsuranceTabSelected = (
    tabLabel: string,
    tabIndex: number,
    primaryInsuranceStatus: string,
  ) => {
    apiWorkerManager.enqueueAndProcess({
      ...getBasePayload(),
      event_name: `${EventsName.INSURANCE_TAB_SELECTED} (${tabLabel})`,
      additional_data: {
        screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
        coverage_type: tabLabel,
        tab_index: tabIndex,
        coverage_status: status?.ev_bv?.primary ?? "",
        primary_insurance_status: primaryInsuranceStatus,
        payer_name: payerName,
      },
    });
  };

  /**
   * Builds the shared additional_data payload for MARK_AS_COMPLETED events.
   * Used by both HappyFlow and CommentRpa save flows.
   *
   * @param screenName - The screen name constant for BigQuery
   * @param modalName - The modal name constant for BigQuery
   * @param masterAuthStatusOld - The previous auth status value (differs by caller)
   * @returns The additional_data object for the event
   */
  const buildMarkAsCompletedAdditionalData = (
    screenName: string,
    modalName: string,
    masterAuthStatusOld: string,
  ) => ({
    screen_name: screenName,
    modal_name: modalName,
    master_auth_status_old:
      generalOrderDetails?.status?.master_auth_status ?? "",
    master_auth_status_new: authOnFileDetails?.auth_status ?? "",
    payer_name: payerName,
    plan_name: primaryCoverage?.plan_name ?? "",
    j_code: (getAllJCodesFromAuthOnFile(authOnFileDetails) ?? [])
      .map((jCode) => jCode?.j_code ?? "")
      .join(","),
    master_auth_status_changed:
      masterAuthStatusOld === authOnFileDetails?.auth_status ? "false" : "true",
    bo_empty: checkIfBusinessOfficeNotesAreEmpty(
      authOnFileDetails?.reports,
    ).toString(),
    regimen_icd_state: submissionData?.regimen_icd_state ?? "",
    old_primary_icd_code: (oldPrimaryIcdCodes ?? []).join(","),
    new_primary_icd_code:
      submissionData?.primary_diagnosis_details
        ?.map((detail) => detail.icd_code ?? "")
        ?.join(",") ?? "",
    icd_codes_changed: (() => {
      const oldCodes = (oldPrimaryIcdCodes ?? []).join(",");
      const newCodes =
        submissionData?.primary_diagnosis_details
          ?.map((detail) => detail.icd_code ?? "")
          ?.join(",") ?? "";
      return oldCodes !== newCodes;
    })(),
    count_of_icd_codes: submissionData?.primary_diagnosis_details?.length ?? 0,
  });

  /**
   * Fires the MARK_AS_COMPLETED_BUTTON_SAVED event.
   *
   * @param screenName - The screen name constant for BigQuery
   * @param modalName - The modal name constant for BigQuery
   * @param masterAuthStatusOld - The previous auth status value
   */
  const fireMarkAsCompletedEvent = (
    screenName: string,
    modalName: string,
    masterAuthStatusOld: string,
  ) => {
    apiWorkerManager.enqueueAndProcess({
      ...getBasePayload(),
      event_name: EventsName.MARK_AS_COMPLETED_BUTTON_SAVED,
      additional_data: buildMarkAsCompletedAdditionalData(
        screenName,
        modalName,
        masterAuthStatusOld,
      ),
    });
  };

  /**
   * Logs J-code status events after a save operation completes.
   *
   * @param delayMs - Delay in milliseconds before processing
   */
  const logJCodesAfterSave = (delayMs: number) => {
    const allJCodes = getAllJCodesFromAuthOnFile(authOnFileDetails);
    if (allJCodes) {
      logJCodeStatusEvents(allJCodes, delayMs);
    }
  };

  return {
    trackInsuranceTabSelected,
    fireMarkAsCompletedEvent,
    logJCodesAfterSave,
    masterAuthStatus: status?.master_auth_status ?? "",
    oldAuthStatusFromContext: oldAuthStatusFromContext ?? "",
    ScreenNamesForBigQuery,
    ModalNamesForBigQuery,
  };
};

/** Return type of the useCoverageAnalytics hook. */
export type CoverageAnalytics = ReturnType<typeof useCoverageAnalytics>;
